import { useEffect, useState } from "react";
import type { TeamMember, RecycleBinItem } from "../../../shared/types";
import { AtlShell, XIcon } from "../../components/atelier";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";

export default function RecycleBin() {
  const [users, setUsers] = useState<TeamMember[]>([]);
  const [items, setItems] = useState<RecycleBinItem[] | null>(null);
  const [target, setTarget] = useState<Record<string, string>>({});

  function refresh() {
    api.listUsers().then((r) => setUsers(r.users)).catch(() => {});
    api.listRecycleBin().then((r) => setItems(r.quizzes)).catch(() => setItems([]));
  }
  useEffect(refresh, []);

  async function restore(it: RecycleBinItem) {
    const ownerId = target[it.id] ?? it.ownerId;
    try {
      await api.restoreFromBin(it.id, ownerId);
      refresh();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not restore");
    }
  }
  async function purge(it: RecycleBinItem) {
    const ok = await confirmDialog({ title: "Delete permanently?", message: `“${it.title}” will be gone for good.`, tone: "danger", confirmLabel: "Delete forever" });
    if (!ok) return;
    try {
      await api.purgeFromBin(it.id);
      refresh();
    } catch {
      /* ignore */
    }
  }
  async function empty() {
    const n = items?.length ?? 0;
    const ok = await confirmDialog({ title: "Empty recycle bin?", message: `All ${n} item${n === 1 ? "" : "s"} will be permanently deleted.`, tone: "danger", confirmLabel: "Empty bin" });
    if (!ok) return;
    try {
      await api.emptyBin();
      refresh();
    } catch {
      /* ignore */
    }
  }

  return (
    <AtlShell active="Recycle bin" title="Recycle bin" sub="RESTORE OR REMOVE DELETED QUIZZES">
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 18, maxWidth: 860 }}>
        <div className="atl-card" style={{ padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
            <div>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>
                Deleted quizzes {items && items.length > 0 ? <span style={{ color: "var(--atl-faint)" }}>({items.length})</span> : ""}
              </h3>
              <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>
                Restore to any account or remove for good.
              </p>
            </div>
            {items && items.length > 0 && (
              <button className="atl-btn atl-btn--link" style={{ marginLeft: "auto", fontSize: 13, color: "var(--atl-accent)" }} onClick={empty}>
                Empty bin
              </button>
            )}
          </div>
          {items === null ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>Loading…</p>
          ) : items.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>Nothing in the bin.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {items.map((it) => (
                <div key={it.id} className="atl-row" style={{ padding: "11px 16px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 600, fontSize: 14.5 }}>{it.title}</div>
                    <div style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}>
                      {it.questionCount} question{it.questionCount === 1 ? "" : "s"}
                      {it.deletedNote ? ` · ${it.deletedNote}` : ""}
                    </div>
                  </div>
                  <select
                    className="atl-select"
                    style={{ width: "auto" }}
                    value={target[it.id] ?? it.ownerId}
                    onChange={(e) => setTarget((t) => ({ ...t, [it.id]: e.target.value }))}
                  >
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                  <button className="atl-btn atl-btn--ghost" style={{ padding: "9px 16px", fontSize: 13 }} onClick={() => restore(it)}>
                    Restore
                  </button>
                  <button className="atl-iconbtn atl-iconbtn--danger" title="Delete permanently" onClick={() => purge(it)}>
                    <XIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AtlShell>
  );
}
